/**
 * Banner derivation per REQ-035:
 *   "Staff UI surfaces all `sync_conflict`, `routing_overridden`,
 *    `external_state_stale_at_booking`, and `pending_reschedule` cases as banners
 *    on the affected appointment."
 *
 * This is pure logic: appointment + open sync conflicts → list of banner descriptors.
 * Pure means we can test it and consumers can render the resulting list however
 * they want (compact tag in a card, full row above the calendar, etc).
 */

import type { Appointment, SyncConflict } from "./types.js";

export interface BannerDescriptor {
  kind: "sync_conflict" | "routing_overridden" | "external_state_stale_at_booking" | "pending_reschedule";
  title: string;
  description: string;
  /** The appointment_id this banner attaches to. */
  appointment_id: string;
  /** Where the action lives, if applicable (e.g., link to sync-conflicts page). */
  action_href?: string;
  /** sync_conflict id to resolve (sync_conflict only). */
  conflict_id?: string;
}

export function deriveBanners(
  appointment: Appointment,
  conflicts: SyncConflict[]
): BannerDescriptor[] {
  const banners: BannerDescriptor[] = [];

  if (appointment.lifecycle_state === "pending_reschedule") {
    banners.push({
      kind: "pending_reschedule",
      title: "Needs reschedule",
      description: "Routed feasibility failed for this instance — pick a new slot.",
      appointment_id: appointment.id,
      action_href: `/appointments/${appointment.id}#reschedule`
    });
  }

  if (appointment.routing_overridden) {
    banners.push({
      kind: "routing_overridden",
      title: "Routing overridden",
      description: "Travel feasibility was overridden when this appointment was booked.",
      appointment_id: appointment.id
    });
  }

  if (appointment.external_state_stale_at_booking) {
    banners.push({
      kind: "external_state_stale_at_booking",
      title: "External calendar was stale at booking",
      description: "Reconnect the external calendar; verify no conflicts exist.",
      appointment_id: appointment.id
    });
  }

  for (const conflict of conflicts) {
    if (conflict.appointment_id === appointment.id && conflict.status === "open") {
      banners.push({
        kind: "sync_conflict",
        title: "Sync conflict",
        description: "An external edit conflicts with this appointment.",
        appointment_id: appointment.id,
        action_href: "/sync-conflicts",
        conflict_id: conflict.id
      });
    }
  }

  return banners;
}
